import { shouldSkipTracking } from '../utils/cookie';
import {
  getGa4GroupId,
  getTrackers,
  prependActionWithTrackerName,
} from './utils';
import { createConfigParametersRepository } from '../../configParametersRepository';
import { GtagEvent } from '../thirdPartyTrackingAgent/types';
import {
  pushStoredEvent,
  removeStoredEvent,
} from '../thirdPartyTrackingAgent/eventStore';

/**
 * Calls Google Universal Analytics `ga` function with the given `args` for all trackers on the page.
 * If the `shouldSkipTracking()` function returns `true`, no tracking is performed.
 *
 * @param {...any[]} args - The arguments to pass to the `ga` function.
 */
export const gaWrapper = (...args) => {
  const trackers = getTrackers();
  const shouldSkip = shouldSkipTracking();
  if (shouldSkip || trackers.length === 0) {
    return;
  }
  const [action, ...rest] = args;
  trackers.forEach((tracker) => {
    const newArgs = [
      trackers.length === 1
        ? action
        : prependActionWithTrackerName(tracker, action),
      ...rest,
    ];
    window.ga?.apply(null, newArgs);
  });
};

/**
 * Calls Google Analytics 4 `gtag` function with the given `args` for all trackers on the page.
 * If the `shouldSkipTracking()` function returns `true`, no tracking is performed.
 *
 * @param {...any[]} args - The arguments to pass to the `gtag` function.
 */
export const gtagWrapper: Gtag.Gtag = (command, ...args) => {
  const { getGenericParameters } = createConfigParametersRepository();
  const { isNativeInterface } = getGenericParameters();

  if (shouldSkipTracking() || isNativeInterface) {
    return;
  }
  if (command === 'event') {
    const [eventName, customParams] = args as [string, GtagEvent[2]];
    const send_to = getGa4GroupId();
    const params: typeof customParams = {
      send_to,
      edo_timestamp: Date.now(), // important to have this before customParams in case it's overwritten
      ...customParams,
    };
    const uuid = pushStoredEvent([command, eventName, params] as GtagEvent);
    window.gtag?.('event', eventName, {
      ...params,
      ...(uuid && { event_callback: () => removeStoredEvent(uuid) }),
    });
    return;
  }
  window.gtag?.(command, ...args);
};
